import mori from 'mori';

// footnoteCallback runs once per table cell, and ag-grid makes several
// passes over the grid per render. Both the set of footnoted locations
// and the per-key result depend only on the footnotes collection, which
// is immutable and stable for the life of a render, so both are cached
// and invalidated together when that collection changes.
const noFootnotesCached = {};
let cachedFootnotes = noFootnotesCached;
let cachedLocations = [];
let cachedResults = Object.create(null);
let cachedBuckets = Object.create(null);
let cachedUnbucketed = mori.vector();

/**
 * Filter footnotes per cell and flatten them
 * @param  {Array} key                [key array for footnotes]
 * @param  {mori.hashMap} footnotes   [footnotes]
 * @return {Array}                    [footnotes for key]
 */
export default function footnoteCallback(key, footnotes, isTrend = false) {
  if (!isLocationInFootnotes(key, footnotes)) {
    // Location isn't in footnotes
    return [];
  }

  // key[1] can itself be an array, so stringify rather than join.
  const cacheKey = `${isTrend}\u0000${JSON.stringify(key)}`;
  const cached = cachedResults[cacheKey];
  if (cached !== undefined) {
    // cellRenderer sorts the result in place, so never hand out the
    // cached instance itself.
    return Array.isArray(cached) ? cached.slice() : cached;
  }

  // Only notes that mention this location can match. Every branch of the
  // predicate below requires key[0] somewhere in the note's key, so the
  // bucket is a safe narrowing of the full set.
  const bucket = cachedBuckets[key[0]];
  const candidates = bucket ?
    mori.concat(bucket, cachedUnbucketed) :
    cachedUnbucketed;

  const result = mori.toJs(mori.distinct(mori.flatten(
    mori.map(
      (row) => mori.flatten(mori.rest(row)),
      mori.filter(
        (note) => {
          // If key[2], then we are coming from cellRenderer and we are on a geoMulti indicator
          // We will match on the entire key - State, Dist., Value
          // We will then attemp to match on just State and Dist.
          if (key[2] && mori.count(mori.first(note)) === 4) {
            return mori.isSubset(
              mori.set(key),
              mori.first(note)
            );
            // No key[2] means we proceed as normal
          } else if (!Array.isArray(key[1])) {
            return mori.isSubset(
              mori.set(key[2] ? key.slice(0, 2) : key),
              mori.first(note)
            );
          }
          return (
            // We need to include only headerGroup, only columnTile, and combined
            mori.isSubset(
              mori.set([
                key[0],
                mori.hashMap(
                  key[1][0],
                  key[1][1]
                ),
              ]),
              mori.first(note)
            ) ||
            mori.isSubset(
              mori.set([key[0], key[1][0]]),
              !isTrend ? mori.first(note) : mori.rest(mori.first(note))
            ) ||
            mori.isSubset(
              mori.set([key[0], key[1][1]]),
              !isTrend ? mori.first(note) : mori.rest(mori.first(note))
            ) || (
              mori.first(mori.first(mori.last(mori.first(note)))) === 'any' &&
              mori.equals(
                mori.set([key[0], key[1][1]]),
                mori.set(
                  [
                    mori.first(mori.rest(mori.first(note))),
                    mori.last(mori.last(mori.last(mori.first(note)))),
                  ]
                ) // set
              ) // equals
            )
          );
        },
        candidates
      )
    )
  )));

  cachedResults[cacheKey] = result;
  return Array.isArray(result) ? result.slice() : result;
}

/**
 * Check if location is in footnotes
 * @param  {Array} key                [key array for footnotes]
 * @param  {mori.hashMap} footnotes   [footnotes]
 * @return {Boolean}                  [if location is in footnotes]
 */
function isLocationInFootnotes(key, footnotes) {
  if (footnotes !== cachedFootnotes) {
    cachedFootnotes = footnotes;
    cachedResults = Object.create(null);
    buildBuckets(footnotes);
    cachedLocations = mori.intoArray(mori.distinct(mori.reduceKV(
      // accumulator, key, value (value isn't needed here)
      (acc, rKey) => mori.conj(acc, mori.first(mori.rest(rKey))),
      mori.vector(),
      footnotes,
    )));
  }

  // indexOf matches the strict equality the previous mori.filter used.
  return cachedLocations.indexOf(key[0]) > -1;
}

/**
 * Collect every scalar reachable inside a converted note key, so a note
 * can be found by any value it mentions, however deeply nested.
 *
 * @param  {*}     node Plain JS value produced by mori.toJs.
 * @param  {Array} out  Accumulator.
 * @return {Array}      out
 */
function collectValues(node, out) {
  if (node === null || node === undefined) {
    return out;
  }
  if (typeof node === 'string' || typeof node === 'number') {
    const value = String(node);
    if (out.indexOf(value) === -1) {
      out.push(value);
    }
    return out;
  }
  if (Array.isArray(node)) {
    node.forEach((item) => collectValues(item, out));
    return out;
  }
  if (typeof node === 'object') {
    Object.keys(node).forEach((prop) => {
      collectValues(prop, out);
      collectValues(node[prop], out);
    });
  }
  return out;
}

/**
 * Index the footnotes by every value their key mentions. Scanning all of
 * them per cell was the dominant cost of ag-grid's row height pass.
 *
 * @param {mori.hashMap} footnotes The labelled footnotes for this render.
 */
function buildBuckets(footnotes) {
  const buckets = Object.create(null);
  const unbucketed = [];

  mori.each(footnotes, (note) => {
    let values = [];
    try {
      values = collectValues(mori.toJs(mori.first(note)), []);
    } catch (error) {
      values = [];
    }
    if (!values.length) {
      // Could not classify it, so it has to be scanned every time.
      unbucketed.push(note);
      return;
    }
    values.forEach((value) => {
      if (!buckets[value]) {
        buckets[value] = [];
      }
      buckets[value].push(note);
    });
  });

  Object.keys(buckets).forEach((value) => {
    buckets[value] = mori.into(mori.vector(), buckets[value]);
  });

  cachedBuckets = buckets;
  cachedUnbucketed = mori.into(mori.vector(), unbucketed);
}



// WEBPACK FOOTER //
// ../~/eslint-loader!./js/services/getFootnotes/footnoteCallback.js